import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import constate from 'constate'
import queryString from 'query-string'
import { OrderDirection } from 'generated/graphql'
import { MOBILE_BUTTON_SIZE, PC_BUTTON_SIZE } from 'constants/paginations'
import usePagination from 'hooks/usePagination'
import { parseValues } from 'utils/utils'

export enum TabState {
  All = 'all',
  OpenActivity = 'open-activity',
  Channel = 'channel',
  NewsItem = 'news-item',
  Community = 'community',
  ClosedActivity = 'closed-activity',
}

export enum SearchOrderField {
  ViewCount = 'VIEW_COUNT',
  CreatedAt = 'CREATED_AT',
  Relevance = 'RELEVANCE',
}

export interface SearchState {
  q?: string
  tab?: TabState
  page?: number
  sort?: string // orderField
  direction?: OrderDirection
}

interface UseSearchStateProps {
  searchState: SearchState
  onSearchStateChange?: (newState: SearchState) => void
}

function initializeState(newState: SearchState, updatedState: SearchState) {
  if (!Object.keys(updatedState).includes('page')) {
    newState.page = 1

    if (!Object.keys(updatedState).includes('sort')) {
      if (newState.tab === TabState.Community) {
        newState.sort = SearchOrderField.CreatedAt
      } else {
        newState.sort = SearchOrderField.Relevance
      }
    }
  }

  return newState
}

function useSearchState(props?: UseSearchStateProps) {
  const { searchState, onSearchStateChange } = props || {}

  function handleSearchStateChange(updatedState: SearchState) {
    let newState = {
      ...searchState,
      ...queryString.parse(window.location.search),
      ...updatedState,
    }

    newState = initializeState(newState, updatedState)
    newState = parseValues(newState, { parseNumbers: true })

    if (onSearchStateChange) {
      onSearchStateChange(newState)
    }
  }

  function setOrderBy(orderField: any) {
    handleSearchStateChange({ sort: orderField })
  }

  function setTabState(newTabState: TabState) {
    handleSearchStateChange({ tab: newTabState })
  }

  function setActivePage(newActivePage: number) {
    handleSearchStateChange({ page: newActivePage })
  }

  const theme = useTheme()
  const isSMDown = useMediaQuery(theme.breakpoints.down('sm'))

  const pagination = usePagination({
    activePage: searchState?.page || 1,
    setActivePage,
    maxButtons: isSMDown ? MOBILE_BUTTON_SIZE : PC_BUTTON_SIZE,
  })

  return {
    query: searchState?.q || '',
    tabState: searchState?.tab || TabState.All,
    setTabState,
    page: searchState?.page || 1,
    pagination,
    orderBy: {
      orderField: searchState?.sort || SearchOrderField.Relevance,
      orderDirection: searchState?.direction || OrderDirection.DESC,
    },
    setOrderBy,
  }
}

export const [SearchStateContextProvider, useSearchStateContext] =
  constate(useSearchState)
